import { tt } from '@agos/shared'

/**
 * What an order's source badge should say - `FRONTEND.md` §9.462.
 *
 * **The owner:** *"there are more orders like Reštaurácia, but still showing Telefón"*, and then
 * *"some of reštuarácia does not have Telefón next to them, for example 16095"* - two halves of one
 * complaint, and the second half was split portions with no `source` at all (§10A.224).
 *
 * **`orders.source` records who typed the order, not how it arrived.** The creation path writes
 * `staff ? "phone" : "web"`, one expression, so **every** order a member of staff rings up is `phone`:
 * 1 961 restaurant tables and 574 bar tabs in the owner's own data, none of which was phoned. The
 * badge then prints *Telefón* beside *Typ: Reštaurácia*, which is a contradiction on one screen.
 *
 * **Legacy does exactly the same** (`lists.tsx:113`), so this is a deliberate improvement over a
 * faithfully ported defect rather than a port error - `agos-the-port-may-match-legacy-and-still-be-wrong`.
 *
 * **Decided by the channel's capability, never by its slug.** `onPremise` is precisely *the customer
 * is here*, so a staff-typed order on such a channel was taken at the counter or the table. A delivery
 * is not on the premises and `Telefón` stays right for it - which is the 1 612 rows where the badge was
 * telling the truth all along.
 *
 * **The stored value is untouched.** `source` still says what it always said and every report that
 * reads it is unaffected; what changes is the sentence a human is shown. Rewriting 2 500 rows to say
 * something the column was never defined to mean would be the worse fix.
 */

export type SourceBadgeKind = 'web' | 'phone' | 'on-premise' | 'none'

/** Only the flag that matters, so a caller need not hold a whole channel. */
export interface BadgeChannel {
  onPremise?: boolean
}

export function sourceBadgeKind(
  source: string | undefined | null,
  channel: BadgeChannel | undefined,
): SourceBadgeKind {
  if (source === 'web') return 'web'
  if (source !== 'phone') return 'none'
  // Absent channel reads as *not on the premises*: the list may draw before `/sales-channels`
  // answers, and falling back to today's wording keeps a slow read from changing what a row says.
  return channel?.onPremise === true ? 'on-premise' : 'phone'
}

/** The word on the badge. */
export function sourceBadgeLabel(kind: SourceBadgeKind): string {
  if (kind === 'web') return 'Web'
  if (kind === 'phone') return tt('Telefón')
  if (kind === 'on-premise') return tt('Na mieste')
  return ''
}
